import '../../i18n/autoTranslate';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SpotlightCard from '../../../components/ui/SpotlightCard';

const NextStepsSection = ({ 
  currentScore = 85,
  weakestAreas = ['Technical Skills', 'Communication'],
  recommendedPractice = [
    {
      type: 'Technical Interview',
      difficulty: 'Medium',
      focus: 'Algorithm explanation and complexity analysis',
      estimatedTime: '45 minutes',
      icon: 'Code'
    },
    {
      type: 'Behavioral Interview',
      difficulty: 'Easy',
      focus: 'STAR method and quantifiable results',
      estimatedTime: '30 minutes',
      icon: 'Users'
    },
    {
      type: 'Communication Practice',
      difficulty: 'Easy',
      focus: 'Filler word reduction and clarity',
      estimatedTime: '20 minutes',
      icon: 'MessageSquare'
    }
  ],
  milestones = [
    { score: 70, label: 'Good Performance', achieved: true },
    { score: 80, label: 'Strong Candidate', achieved: true },
    { score: 90, label: 'Exceptional Performance', achieved: false },
    { score: 95, label: 'Interview Master', achieved: false }
  ],
  isLoading = false
}) => {
  const navigate = useNavigate();
  const [selectedPractice, setSelectedPractice] = useState(null);

  const handleStartPractice = (practiceType) => {
    navigate('/interview-setup-configuration', { 
      state: { 
        preselectedType: practiceType?.type,
        focusArea: practiceType?.focus 
      }
    });
  };

  const handleViewProgress = () => {
    navigate('/progress-analytics-insights');
  };

  const handleRetakeInterview = () => {
    navigate('/interview-setup-configuration');
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return 'text-green-400 border-green-500/20 bg-green-500/10';
      case 'Medium':
        return 'text-yellow-400 border-yellow-500/20 bg-yellow-500/10';
      case 'Hard':
        return 'text-red-400 border-red-500/20 bg-red-500/10';
      default:
        return 'text-gray-400 border-gray-600 bg-gray-800/20';
    }
  };

  const getNextMilestone = () => {
    return milestones?.find(m => !m?.achieved);
  };

  const nextMilestone = getNextMilestone();

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2]?.map(i => (
          <SpotlightCard key={i} spotlightColor="rgba(139, 92, 246, 0.15)">
            <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10  p-6 animate-pulse">
              <div className="space-y-4">
                <div className="h-6 bg-gray-700/50 rounded w-48"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="h-32 bg-gray-700/50 rounded-lg"></div>
                  <div className="h-32 bg-gray-700/50 rounded-lg"></div>
                </div>
              </div>
            </div>
          </SpotlightCard>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress Milestones */}
      <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
        <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10  p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-white">Progress Milestones</h3>
              <p className="text-sm text-gray-400 mt-1">
                Track your journey to interview mastery
              </p>
            </div>
            <Icon name="Trophy" size={24} className="text-indigo-400" />
          </div>

          <div className="space-y-4">
            {/* Current Progress */}
            <div className="flex items-center justify-between p-4 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-indigo-500 text-white">
                  <Icon name="User" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-white">Current Score</h4>
                  <p className="text-sm text-gray-400">Your latest performance</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-indigo-400">{currentScore}</div>
                <div className="text-xs text-gray-400">out of 100</div>
              </div>
            </div>

            {/* Milestone Progress */}
            <div className="space-y-3">
              {milestones?.map((milestone, index) => (
                <div
                  key={milestone?.score}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-all duration-200 ${
                    milestone?.achieved
                      ? 'border-green-500/20 bg-green-500/5'
                      : milestone?.score === nextMilestone?.score
                      ? 'border-indigo-500/20 bg-indigo-500/5' 
                      : 'border-gray-700 bg-gray-800/20'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                      milestone?.achieved
                        ? 'bg-green-500 text-white'
                        : milestone?.score === nextMilestone?.score
                        ? 'bg-indigo-500 text-white' 
                        : 'bg-gray-700 text-gray-400'
                    }`}>
                      {milestone?.achieved ? (
                        <Icon name="Check" size={16} />
                      ) : milestone?.score === nextMilestone?.score ? (
                        <Icon name="Target" size={16} />
                      ) : (
                        <Icon name="Lock" size={16} />
                      )}
                    </div>
                    <div>
                      <h5 className="font-medium text-white">{milestone?.label}</h5>
                      <p className="text-xs text-gray-400">Score {milestone?.score}+</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {milestone?.achieved && (
                      <span className="text-xs text-green-400 font-medium">Achieved</span>
                    )}
                    {milestone?.score === nextMilestone?.score && (
                      <span className="text-xs text-indigo-400 font-medium">
                        {milestone?.score - currentScore} points to go
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </SpotlightCard>

      {/* Recommended Practice */}
      <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
        <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10  p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-white">Recommended Practice</h3>
              <p className="text-sm text-gray-400 mt-1">
                Targeted sessions to improve your weakest areas: {weakestAreas?.join(', ')}
              </p>
            </div>
            <Icon name="Target" size={24} className="text-indigo-400" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommendedPractice?.map((practice, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                  selectedPractice === index
                    ? 'border-indigo-500/50 bg-indigo-500/5 shadow-lg'
                    : 'border-gray-700 hover:border-gray-600 hover:bg-white/5'
                }`}
                onClick={() => setSelectedPractice(selectedPractice === index ? null : index)}
              >
                <div className="flex items-start space-x-3 mb-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-500/20">
                    <Icon name={practice?.icon} size={20} className="text-indigo-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-white">{practice?.type}</h4>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium border mt-2 ${getDifficultyColor(practice?.difficulty)}`}>
                      {practice?.difficulty}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-400 mb-2">Focus Area:</p>
                    <p className="text-sm text-white">{practice?.focus}</p>
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={12} />
                      <span>{practice?.estimatedTime}</span>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleStartPractice(practice);
                    }}
                    iconName="Play"
                    className="border-gray-600 text-gray-300 hover:text-white"
                  >
                    Start Practice
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </SpotlightCard>

      {/* Quick Actions */}
      <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
        <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10  p-6">
          <h3 className="text-xl font-bold text-white mb-6">Quick Actions</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              variant="default"
              fullWidth
              onClick={handleRetakeInterview}
              iconName="RotateCcw"
              className="h-16 flex-col space-y-2 bg-gradient-to-r from-purple-500 to-indigo-600"
            >
              <span className="font-semibold">Retake Interview</span>
              <span className="text-xs opacity-80">Same questions, track improvement</span>
            </Button>

            <Button
              variant="outline"
              fullWidth
              onClick={handleViewProgress}
              iconName="TrendingUp"
              className="h-16 flex-col space-y-2 border-gray-600 text-gray-300 hover:text-white"
            >
              <span className="font-semibold">View Progress</span>
              <span className="text-xs opacity-80">Detailed analytics & trends</span>
            </Button>

            <Button
              variant="outline"
              fullWidth
              onClick={() => navigate('/practice-library-resources')}
              iconName="BookOpen"
              className="h-16 flex-col space-y-2 border-gray-600 text-gray-300 hover:text-white"
            >
              <span className="font-semibold">Practice Library</span>
              <span className="text-xs opacity-80">Browse more questions</span>
            </Button>
          </div>
        </div>
      </SpotlightCard>

      {/* Motivational Message */}
      <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
        <div className=" border border-white/10  p-6 bg-gradient-to-r from-indigo-500/5 to-green-500/5">
          <div className="flex items-start space-x-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-indigo-500 to-green-500 text-white">
              <Icon name="Sparkles" size={24} />
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-white mb-2">
                {currentScore >= 80 ? 'Excellent Work!' : 'Keep Going!'}
              </h4>
              <p className="text-gray-300">
                {currentScore >= 80 
                  ? `You're performing at a high level! Focus on the recommended practice areas to reach the next milestone and achieve interview mastery.`
                  : `You're making great progress! Regular practice in your identified weak areas will help you reach the next performance level quickly.`
                }
              </p>
              {nextMilestone && (
                <div className="mt-4 p-3 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
                  <p className="text-sm text-indigo-400 font-medium">
                    Next Goal: {nextMilestone?.label} ({nextMilestone?.score - currentScore} points to go)
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </SpotlightCard>
    </div>
  );
};

export default NextStepsSection;
